import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Routes, Route, useNavigate, Link, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { CalendarDays, Clock, MapPin, Navigation, Camera, Pen, CheckCircle2, ArrowLeft, LogOut, Play, Pause, FileSignature, ChevronRight, Phone, MessageSquare, GraduationCap, LayoutDashboard, Loader2, AlertCircle, X } from 'lucide-react';
import XpertLogo from '../components/XpertLogo';
import MessagingView from '../components/MessagingView';
import CEStudentView from '../components/CEStudentView';
import { messaging, team, apiWorkorders, jobApi, uploads, pesticideProducts } from '../lib/api';
import PesticideLog, { emptyLine, toApplications } from '../components/tech/PesticideLog';
import { toast } from 'sonner';
import { normDate, today, shiftDays, prettyDate } from '../lib/dates';
import { enqueue, isNetworkError, cacheJobs, cachedJobs } from '../lib/offlineQueue';
import OfflineBanner from '../components/tech/OfflineBanner';
import StationScanner from '../components/tech/StationScanner';
import { smsHref, onMyWayText } from '../lib/smsLink';

// "9:30 AM", "09:30" or "" → minutes since midnight, for ordering a day's stops.
const timeKey = (t) => {
  const m = /(\d{1,2}):(\d{2})\s*(am|pm)?/i.exec(String(t || ''));
  if (!m) return 24 * 60;
  const ap = (m[3] || '').toLowerCase();
  const h = ap ? (Number(m[1]) % 12) + (ap === 'pm' ? 12 : 0) : Number(m[1]);
  return h * 60 + Number(m[2]);
};

const statusColor = (s) => ({ Scheduled:'bg-slate-100 text-slate-700','In Progress':'bg-blue-100 text-blue-700','En Route':'bg-amber-100 text-amber-700', Completed:'bg-emerald-100 text-emerald-700' }[s]);

function TechHeader({ user, onLogout, title='My Day', back }) {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const poll = () => messaging.unreadCount().then(d => setUnread(d.unread || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="sticky top-0 z-40 bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] text-white">
      <div className="px-4 pt-5 pb-4 flex items-center gap-2">
        {back ? <button onClick={()=>navigate(-1)} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><ArrowLeft className="w-5 h-5"/></button> : <XpertLogo size={36} light/>}
        <div className="flex-1"><div className="text-xs text-white/70 font-semibold tracking-widest">XPERTPESTS FIELD</div><div className="text-lg font-extrabold">{title}</div></div>
        {/* Owners who also run jobs switch back to the office screens from here. */}
        {(user?.role === 'admin' || user?.role === 'staff') && <button onClick={()=>navigate('/app')} className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center" data-testid="tech-office-btn" aria-label="Office" title="Office"><LayoutDashboard className="w-5 h-5"/></button>}
        <button onClick={()=>navigate('/tech/ce')} className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center" data-testid="tech-ce-btn" aria-label="Training"><GraduationCap className="w-5 h-5"/></button>
        <button onClick={()=>navigate('/tech/messages')} className="relative w-11 h-11 rounded-full bg-white/15 flex items-center justify-center" data-testid="tech-messages-btn" aria-label="Messages">
          <MessageSquare className="w-4 h-4"/>
          {unread > 0 && <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-4.5 h-[18px] px-1 rounded-full bg-[#F59E0B] text-white text-[10px] font-bold ring-2 ring-[#8BC53F]" data-testid="tech-unread-badge">{unread}</span>}
        </button>
        <button onClick={onLogout} className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><LogOut className="w-4 h-4"/></button>
      </div>
    </div>
  );
}

function TechHome({ user, onLogout, jobs: allJobs, loadError, onRetry, savedAt }) {
  // Only the chosen day's stops, in time order — the full list holds every job
  // ever assigned (hundreds after the GorillaDesk import).
  const [day, setDay] = useState(today());
  // The office's saved route order for the day wins; otherwise appointment time.
  const routeKey = (j) => (j.routeDate === day && j.routeOrder ? j.routeOrder : 999);
  const jobs = allJobs.filter(j => normDate(j.date) === day).sort((a, b) => routeKey(a) - routeKey(b) || timeKey(a.time) - timeKey(b.time));
  const completed = jobs.filter(j => j.status==='Completed').length;
  const total = jobs.length;
  const isToday = day === today();
  return (
    <div className="max-w-md mx-auto bg-slate-50 min-h-screen">
      <TechHeader user={user} onLogout={onLogout}/>
      <OfflineBanner onSynced={onRetry} savedAt={savedAt}/>
      <div className="px-4 py-5">
        <Card className="p-5 border-slate-200 bg-gradient-to-br from-white to-emerald-50">
          <div className="flex items-center justify-between"><div><div className="text-xs text-slate-500">Hello,</div><div className="text-xl font-extrabold text-slate-900">{user?.name || 'Technician'}</div></div><div className="text-right"><div className="text-3xl font-extrabold text-[#8BC53F] tabular-nums">{completed}/{total}</div><div className="text-xs text-slate-500">jobs done</div></div></div>
          <div className="mt-4 h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#8BC53F] to-[#F59E0B]" style={{width:`${(completed/total)*100||0}%`}}/></div>
        </Card>
        <div className="mt-6 mb-3 flex items-center justify-between px-1">
          <button onClick={() => setDay(shiftDays(day, -1))} className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center" aria-label="Previous day"><ArrowLeft className="w-4 h-4"/></button>
          <button onClick={() => setDay(today())} className="text-center">
            <div className="font-bold text-slate-900">{isToday ? 'Today’s Stops' : prettyDate(day)}</div>
            {!isToday && <div className="text-[11px] text-[#6FA82C] font-semibold">Back to today</div>}
          </button>
          <button onClick={() => setDay(shiftDays(day, 1))} className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center" aria-label="Next day"><ChevronRight className="w-4 h-4"/></button>
        </div>
        <div className="space-y-3">
          {loadError && (
            <Card className="p-6 text-center border-amber-200 bg-amber-50" data-testid="tech-load-error">
              <AlertCircle className="w-7 h-7 text-amber-500 mx-auto mb-2"/>
              <p className="text-slate-700 text-sm">Couldn’t load your jobs — check your signal.</p>
              <Button size="sm" className="mt-3 bg-[#8BC53F] hover:bg-[#6FA82C]" onClick={onRetry}>Try again</Button>
            </Card>
          )}
          {!loadError && jobs.length === 0 && (
            <Card className="p-8 text-center border-slate-200" data-testid="tech-no-jobs">
              <CalendarDays className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
              <p className="text-slate-500 text-sm">{allJobs.length ? `No stops on ${isToday ? 'today' : prettyDate(day)}.` : 'No jobs assigned to you yet. Your dispatcher assigns work orders to your name in the office app.'}</p>
            </Card>
          )}
          {jobs.map((j, i) => (
            <Link key={j.id} to={`/tech/job/${j.id}`} className="block">
              <Card className="pp-lift p-4 border-slate-200">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center"><div className="w-9 h-9 rounded-full bg-[#8BC53F] text-white font-bold flex items-center justify-center text-sm">{i+1}</div><div className="text-[10px] font-bold text-slate-500 mt-1">{j.time}</div>{j.routeDate === day && j.routeEta && j.routeEta !== j.time ? <div className="text-[9px] text-slate-400">ETA {j.routeEta}</div> : null}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">{j.customer}</div>
                    <div className="text-xs text-slate-600 truncate">{j.service}</div>
                    {/* The address wraps rather than clipping. Several accounts here
                        are one name over many properties - five Nikar addresses, the
                        duplex front and back units - and the part that says which one
                        is at the END of the line, so truncating cut off exactly the
                        word the technician needs. An address with nothing in it says
                        so, instead of leaving a blank line that reads as a glitch. */}
                    <div className="flex items-start gap-1 mt-1.5 text-[11px] text-slate-500">
                      <MapPin className="w-3 h-3 flex-shrink-0 mt-px"/>
                      {j.address
                        ? <span className="line-clamp-2">{j.address}</span>
                        : <span className="text-amber-600 font-semibold">No address on file</span>}
                    </div>
                    <Badge className={`${statusColor(j.status)} border-0 text-[10px] font-bold mt-2`}>{j.status}</Badge>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0"/>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function TechJob({ user, onLogout, jobs, reload, updateLocalJob }) {
  const { id } = useParams();
  const job = jobs.find(j => j.id === id);
  const [phase, setPhase] = useState(job?.status === 'Completed' ? 'done' : (job?.status === 'In Progress' ? 'working' : 'pending'));
  // Each entry is { key, preview, id?, error? }. `id` arrives from the server;
  // until it does the photo is on screen but not yet saved anywhere, and the
  // card says so rather than implying the work is filed.
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [target, setTarget] = useState('');
  // The service report's pesticide rows. One blank row is offered so the form
  // reads as something to fill in rather than something to find.
  const [pestLines, setPestLines] = useState([emptyLine()]);
  const [noneUsed, setNoneUsed] = useState(false);
  const [vocab, setVocab] = useState(null);
  const [signature, setSignature] = useState(false);
  const [signedBy, setSignedBy] = useState('');
  const [noSignature, setNoSignature] = useState(false);
  // After a refresh the job list arrives after this screen; pick up where the
  // job actually is instead of offering "Check In" on work already started.
  useEffect(() => {
    if (job?.status === 'Completed') setPhase((p) => (p === 'pending' ? 'done' : p));
    else if (job?.status === 'In Progress') setPhase((p) => (p === 'pending' ? 'working' : p));
  }, [job?.status]);
  const [saving, setSaving] = useState(false);
  const [startAt, setStartAt] = useState(null);
  const [endAt, setEndAt] = useState(null);
  const sigRef = useRef(null);
  const previews = useRef([]);

  // Object URLs are a browser-wide allocation, not React state; without this
  // every job a technician opens leaks the memory of its photos for the rest
  // of the shift.
  useEffect(() => () => { previews.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  // Site and method wording comes from the server's controlled vocabulary, so
  // what a technician taps is what the DPR summary groups on. If it does not
  // load the form still works — those two fields are detail, not the record.
  useEffect(() => { pesticideProducts.vocab().then(setVocab).catch(() => {}); }, []);

  // Photos already attached to this job, so reopening it shows what was
  // captured instead of an empty grid. Fetched through the API client rather
  // than set as an <img src>, because the download route needs the bearer
  // token and an <img> tag cannot carry one.
  const jobId = job?.id;
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    uploads.list({ linkedType: 'workorder', linkedId: jobId, kind: 'photo' })
      .then(async (rows) => {
        for (const r of rows || []) {
          try {
            const url = await uploads.blobUrl(r.id);
            if (cancelled) { URL.revokeObjectURL(url); return; }
            previews.current.push(url);
            setPhotos((prev) => (prev.some((p) => p.id === r.id) ? prev : [...prev, { key: r.id, id: r.id, preview: url }]));
          } catch { /* a photo that will not load is shown as missing, not as an error the tech must clear */ }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [jobId]);

  if (!job) return <div className="p-8 text-center text-slate-500">Job not found.</div>;

  const persist = async (patch) => {
    updateLocalJob && updateLocalJob(job.id, patch);
    try { await apiWorkorders.update(job.id, { ...job, ...patch }); reload && reload(); }
    catch (err) {
      if (isNetworkError(err)) { await enqueue('checkin', job.id, { ...job, ...patch }); toast.success('No signal - saved on the phone, it will sync'); }
      else toast.error(err?.response?.data?.detail || 'Could not sync');
    }
  };
  const checkIn = () => { setStartAt(new Date()); setPhase('working'); persist({ status: 'In Progress', checkedInAt: new Date().toISOString() }); toast.success('Checked in'); };
  // Photos upload as they are taken rather than at the end. A technician with
  // one bar of signal finds out about a failed photo while still standing at
  // the property, not after tapping Complete.
  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const pending = files.map((f) => {
      const preview = URL.createObjectURL(f);
      previews.current.push(preview);
      return { key: `${Date.now()}-${Math.random()}`, preview, file: f };
    });
    setPhotos((prev) => [...prev, ...pending]);
    for (const p of pending) {
      try {
        const res = await uploads.upload(p.file, { kind: 'photo', linkedType: 'workorder', linkedId: job.id });
        const saved = res?.files?.[0];
        setPhotos((prev) => prev.map((x) => (x.key === p.key ? { ...x, id: saved?.id, file: undefined } : x)));
      } catch (err) {
        if (isNetworkError(err)) {
          // Kept on the phone and sent with the job when signal returns.
          setPhotos((prev) => prev.map((x) => (x.key === p.key ? { ...x, queued: true } : x)));
          continue;
        }
        const detail = err?.response?.data?.detail || 'Upload failed — tap to retry';
        setPhotos((prev) => prev.map((x) => (x.key === p.key ? { ...x, error: detail } : x)));
        toast.error(detail);
      }
    }
  };

  const retryPhoto = async (key) => {
    const p = photos.find((x) => x.key === key);
    if (!p?.file) return;
    setPhotos((prev) => prev.map((x) => (x.key === key ? { ...x, error: undefined } : x)));
    try {
      const res = await uploads.upload(p.file, { kind: 'photo', linkedType: 'workorder', linkedId: job.id });
      setPhotos((prev) => prev.map((x) => (x.key === key ? { ...x, id: res?.files?.[0]?.id, file: undefined } : x)));
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed — tap to retry';
      setPhotos((prev) => prev.map((x) => (x.key === key ? { ...x, error: detail } : x)));
    }
  };

  const dropPhoto = (key) => {
    const p = photos.find((x) => x.key === key);
    setPhotos((prev) => prev.filter((x) => x.key !== key));
    if (p?.id) uploads.remove(p.id).catch(() => {});
  };

  const chemicalSummary = noneUsed
    ? 'None applied'
    : toApplications(pestLines).map((a) => `${a.productName} ${a.amount} ${a.unit}`).join('; ');

  const finish = async () => {
    if (!signature && !noSignature) { toast.error('Capture the customer signature, or tick "Customer not present"'); return; }
    // Checked before the signature is uploaded, so a missing line is fixed while
    // the technician is still on the form rather than after the job is closed.
    if (!(notes || job.notes || '').trim()) {
      toast.error(noSignature ? 'Add a note on how the service was confirmed (customer not present)' : 'Add a note about what was done');
      return;
    }
    if (!noneUsed && !toApplications(pestLines).length) {
      toast.error('Add the product and amount applied, or tick "no pesticide used"');
      return;
    }
    if (photos.some((p) => !p.id && !p.error && !p.queued)) { toast.error('Photos are still uploading'); return; }
    if (photos.some((p) => p.error)) { toast.error('A photo failed to upload — retry or remove it'); return; }
    setSaving(true);
    // The signature is drawn as vectors but stored as a PNG: an SVG is a
    // document the browser executes, and this one gets served back to
    // anyone who opens the record for the next several years.
    const sigBlob = noSignature ? null : await sigRef.current?.toPngBlob();
    if (!noSignature && !sigBlob) { toast.error('Could not read the signature — please sign again'); setSaving(false); return; }
    const payload = {
      notes: notes || job.notes,
      targetPests: target,
      // The work order's own `chemical` line is what the office views and the
      // customer's service history still read, so it is kept in step with the
      // report's rows rather than left as whatever was typed before.
      chemical: chemicalSummary,
      photoIds: photos.map((p) => p.id).filter(Boolean),
      signedBy: noSignature ? 'Customer not present' : signedBy.trim(),
    };
    const reportBody = {
      notes: notes || job.notes || '',
      targetPests: target,
      pesticides: toApplications(pestLines),
      noPesticideUsed: noneUsed,
    };

    // No signal: the completion, its photos, signature and service report wait
    // on the phone and are sent in that order when the connection returns.
    const saveOnPhone = async () => {
      const files = photos.filter((p) => p.queued && p.file).map((p, i) => ({
        field: 'photo', blob: p.file, name: p.file.name || `photo-${job.id}-${i}.jpg`,
        meta: { kind: 'photo', linkedType: 'workorder', linkedId: job.id },
      }));
      if (sigBlob && !payload.signatureId) {
        files.push({ field: 'signature', blob: sigBlob, name: `signature-${job.id}.png`, meta: { kind: 'signature', linkedType: 'workorder', linkedId: job.id } });
      }
      await enqueue('complete', job.id, payload, files);
      await enqueue('report', job.id, reportBody);
      updateLocalJob && updateLocalJob(job.id, { status: 'Completed', pendingSync: true });
      setEndAt(new Date()); setPhase('done');
      toast.success('No signal — job saved on the phone. It will sync automatically.');
    };

    try {
      if (photos.some((p) => p.queued)) throw Object.assign(new Error('photos waiting on the phone'), { offline: true });
      if (sigBlob) {
        const sig = await uploads.upload(
          new File([sigBlob], `signature-${job.id}.png`, { type: 'image/png' }),
          { kind: 'signature', linkedType: 'workorder', linkedId: job.id },
        );
        payload.signatureId = sig?.files?.[0]?.id;
      }
      await jobApi.complete(job.id, payload);
      setEndAt(new Date()); setPhase('done');

      // The job is closed out; the service report is what releases its invoice.
      // It is filed as a second step on purpose: if this call fails the visit is
      // still recorded with its photos and signature, and the job simply sits on
      // the end-of-day list until the report is filed. The reverse — losing the
      // proof of service because the report failed — is the one we cannot undo.
      let report;
      try {
        report = await jobApi.serviceReport(job.id, reportBody);
      } catch (err) {
        if (isNetworkError(err)) {
          await enqueue('report', job.id, reportBody);
          toast.success('Job completed · the service report will file when signal returns');
          return;
        }
        reload && reload();
        toast.error(err?.response?.data?.detail
          || 'Job saved, but the service report did not — the invoice is on hold until you file it');
        return;
      }
      reload && reload();
      toast.success(report?.invoice ? 'Job completed · report filed · invoice created' : 'Job completed · report filed');
    } catch (e) {
      if (e.offline || isNetworkError(e)) {
        await saveOnPhone();
      } else {
        // The job stays open on failure. Showing the "Service Complete" card for
        // work the server never recorded is how proof of service went missing.
        toast.error(e?.response?.data?.detail || 'Could not complete job — nothing was saved, try again');
      }
    } finally {
      setSaving(false);
    }
  };
  const navAddr = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address || job.customer)}`;
  const phone = (job.phone || job.customerPhone || '').replace(/[^\d+]/g, '');

  return (
    <div className="max-w-md mx-auto bg-slate-50 min-h-screen pb-32">
      <TechHeader user={user} onLogout={onLogout} title={job.id} back/>
      <OfflineBanner onSynced={reload}/>
      <div className="px-4 py-5 space-y-4">
        <Card className="p-5 border-slate-200">
          <Badge className={`${statusColor(job.status)} border-0 mb-3`}>{job.status}</Badge>
          <div className="font-extrabold text-slate-900 text-xl">{job.customer}</div>
          <div className="text-sm text-slate-600">{job.service}</div>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400"/><span>{job.address}</span></div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400"/><span>{job.time}</span></div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <Button asChild variant="outline" className="gap-1.5 px-2"><a href={navAddr} target="_blank" rel="noreferrer" data-testid="tech-navigate-btn"><Navigation className="w-4 h-4"/>Navigate</a></Button>
            <Button asChild variant="outline" className="gap-1.5 px-2" disabled={!phone}><a href={phone ? `tel:${phone}` : undefined} data-testid="tech-call-btn"><Phone className="w-4 h-4"/>Call</a></Button>
            <Button asChild variant="outline" className="gap-1.5 px-2" disabled={!phone}><a href={smsHref(phone, onMyWayText(user?.name))} data-testid="tech-text-btn"><MessageSquare className="w-4 h-4"/>Text</a></Button>
          </div>
          {!phone && <p className="mt-2 text-xs text-slate-400">No phone number on this customer.</p>}
        </Card>

        {phase === 'pending' && (
          <Button onClick={checkIn} className="w-full h-14 text-base bg-[#F59E0B] hover:bg-[#D4870A] gap-2 font-bold"><Play className="w-5 h-5"/>Check In &amp; Start Service</Button>
        )}

        {phase === 'working' && (
          <>
            <Card className="p-5 border-slate-200">
              <div className="flex justify-between items-center mb-3"><h3 className="font-bold text-slate-900">Service Notes</h3><Badge className="bg-blue-100 text-blue-700 border-0">In Progress · started {startAt?.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})}</Badge></div>
              <div className="space-y-3">
                <div><label className="text-xs font-semibold text-slate-700">Target Pests</label><Input value={target} onChange={e=>setTarget(e.target.value)} className="mt-1"/></div>
                <div><label className="text-xs font-semibold text-slate-700">Findings &amp; Notes</label><Textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} className="mt-1" placeholder="Conditions observed, treatment areas, recommendations…"/></div>
              </div>
            </Card>
            <Card className="p-5 border-slate-200">
              <h3 className="font-bold text-slate-900">Pesticides Applied</h3>
              <p className="text-xs text-slate-500 mb-3">This is the CA DPR record for the visit. The invoice is raised once it is filed.</p>
              <PesticideLog
                lines={pestLines}
                onChange={setPestLines}
                noneUsed={noneUsed}
                onNoneUsed={setNoneUsed}
                vocab={vocab}
              />
            </Card>
            <Card className="p-5 border-slate-200">
              <h3 className="font-bold text-slate-900 mb-3">Photos &amp; Proof of Service</h3>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p)=>(
                  <div key={p.key} className="relative aspect-square">
                    <img src={p.preview} alt="Service photo" className="w-full h-full object-cover rounded-lg border border-slate-200"/>
                    {p.queued && (
                      <div className="absolute inset-x-0 bottom-0 rounded-b-lg bg-slate-900/70 text-white text-[9px] font-bold text-center py-0.5">On phone</div>
                    )}
                    {!p.id && !p.error && !p.queued && (
                      <div className="absolute inset-0 rounded-lg bg-slate-900/45 flex items-center justify-center" data-testid="tech-photo-uploading">
                        <Loader2 className="w-5 h-5 text-white animate-spin"/>
                      </div>
                    )}
                    {p.error && (
                      <button onClick={()=>retryPhoto(p.key)} title={p.error}
                        className="absolute inset-0 rounded-lg bg-red-900/60 text-white flex flex-col items-center justify-center gap-1 px-1"
                        data-testid="tech-photo-failed">
                        <AlertCircle className="w-5 h-5"/><span className="text-[9px] font-bold leading-tight text-center">Not saved<br/>Tap to retry</span>
                      </button>
                    )}
                    <button onClick={()=>dropPhoto(p.key)} aria-label="Remove photo"
                      className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white border border-slate-300 text-slate-600 flex items-center justify-center shadow-sm">
                      <X className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                ))}
                <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:border-[#8BC53F] hover:text-[#8BC53F] cursor-pointer transition">
                  <Camera className="w-6 h-6"/><span className="text-[10px] mt-1 font-semibold">Add Photo</span>
                  <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={onPhoto}/>
                </label>
              </div>
            </Card>
            {job.customerId && (
              <Card className="p-5 border-slate-200">
                <h3 className="font-bold text-slate-900 mb-3">Bait Stations &amp; Devices</h3>
                <StationScanner customerId={job.customerId} customerName={job.customer} techName={user?.name || ''} jobId={job.id}/>
              </Card>
            )}
            <Card className="p-5 border-slate-200">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><FileSignature className="w-4 h-4 text-[#8BC53F]"/>Customer Signature</h3>
              <label className="flex items-center gap-2 text-sm text-slate-700 mb-3"><input type="checkbox" checked={noSignature} onChange={e=>setNoSignature(e.target.checked)} className="w-4 h-4 accent-[#8BC53F]" data-testid="tech-no-signature"/>Customer not present (exterior / no one home)</label>
              {!noSignature && <SignaturePad ref={sigRef} signed={signature} onSign={setSignature}/>}
              <div className="mt-3"><label className="text-xs font-semibold text-slate-700">Name of person signing</label>
                <Input value={signedBy} onChange={e=>setSignedBy(e.target.value)} className="mt-1" placeholder="Who signed for the customer" data-testid="tech-signed-by"/></div>
              {signature && <div className="flex items-center gap-2 text-sm text-emerald-600 mt-2 font-semibold"><CheckCircle2 className="w-4 h-4"/>Signed by customer</div>}
            </Card>
            <Button onClick={finish} disabled={saving} className="w-full h-14 text-base bg-[#8BC53F] hover:bg-[#6FA82C] gap-2 font-bold" data-testid="tech-complete-btn">
              {saving ? <><Loader2 className="w-5 h-5 animate-spin"/>Saving…</> : <><CheckCircle2 className="w-5 h-5"/>Complete Service</>}
            </Button>
          </>
        )}

        {phase === 'done' && (
          <Card className="p-6 border-emerald-200 bg-emerald-50 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto"><CheckCircle2 className="w-7 h-7"/></div>
            <h3 className="font-extrabold text-emerald-900 text-lg mt-3">Service Complete</h3>
            <p className="text-sm text-emerald-800 mt-1">{job.pendingSync ? 'Saved on this phone - it will reach the office as soon as you have signal.' : 'Proof of service saved to the job record.'}</p>
            <p className="text-xs text-emerald-800 mt-1" data-testid="tech-proof-summary">
              {photos.filter((p)=>p.id).length} photo(s){signature ? ' · signature captured' : ''}
            </p>
            <div className="mt-4 text-xs text-emerald-800">{startAt?.toLocaleTimeString()} → {(endAt||new Date()).toLocaleTimeString()}</div>
            <Button asChild className="mt-4 bg-[#8BC53F] hover:bg-[#6FA82C]"><Link to="/tech">Back to My Day</Link></Button>
          </Card>
        )}
      </div>
    </div>
  );
}

// The pad keeps its strokes as points rather than as SVG path strings, because
// the same points have to serve two masters: the SVG the customer watches
// while signing, and the PNG the server stores. PNG, not SVG — an SVG is a
// document the browser will execute, and this file gets served back to whoever
// opens the record years from now.
const SignaturePad = forwardRef(function SignaturePad({ signed, onSign }, ref) {
  const [strokes, setStrokes] = useState([]);
  const [current, setCurrent] = useState([]);
  const drawing = useRef(false);
  const svgRef = useRef(null);

  const pt = (e) => {
    const t = e.touches ? e.touches[0] : e;
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; setCurrent([pt(e)]); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pt(e); setCurrent((c) => [...c, p]); };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setCurrent((c) => {
      // A single tap is a stray touch, not a signature.
      if (c.length > 1) { setStrokes((s) => [...s, c]); onSign(true); }
      return [];
    });
  };
  const clear = () => { setStrokes([]); setCurrent([]); onSign(false); };

  const d = (pts) => pts.map((p, i) => `${i ? 'L' : 'M'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

  useImperativeHandle(ref, () => ({
    isEmpty: () => strokes.length === 0,
    // Rasterise at 2x so the signature stays legible when the service report is
    // printed rather than viewed on a phone.
    toPngBlob: () => new Promise((resolve) => {
      if (!strokes.length) { resolve(null); return; }
      const box = svgRef.current?.getBoundingClientRect();
      const w = Math.round(box?.width || 320);
      const h = Math.round(box?.height || 160);
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = w * scale; canvas.height = h * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.scale(scale, scale);
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = '#111827'; ctx.lineWidth = 2.5;
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      strokes.forEach((pts) => {
        ctx.beginPath();
        pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.stroke();
      });
      canvas.toBlob((b) => resolve(b), 'image/png');
    }),
  }), [strokes]);

  return (
    <div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white">
        <svg ref={svgRef} width="100%" height={160} className="touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} onTouchCancel={end}
          data-testid="tech-signature-pad">
          {strokes.map((pts, i) => <path key={i} d={d(pts)} stroke="#8BC53F" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>)}
          {current.length > 0 && <path d={d(current)} stroke="#8BC53F" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>}
          {!strokes.length && !current.length && <text x="50%" y="50%" textAnchor="middle" className="fill-slate-300 text-sm" dy="0.3em">Sign here</text>}
        </svg>
      </div>
      <button onClick={clear} className="text-xs text-slate-500 mt-2 hover:text-slate-700" data-testid="tech-signature-clear">Clear</button>
    </div>
  );
});

export default function TechApp() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // A change made on the phone shows straight away, and survives a reload while offline.
  const updateLocalJob = React.useCallback((id, patch) => {
    setJobs((prev) => { const next = prev.map((j) => (j.id === id ? { ...j, ...patch } : j)); cacheJobs(next); return next; });
  }, []);

  const loadJobs = React.useCallback((u) => {
    apiWorkorders.list().then((all) => {
      const mine = (all || []).filter((w) => {
        if (u?.techId && w.techId && w.techId === u.techId) return true;
        if (u?.name && w.tech && String(w.tech).toLowerCase() === String(u.name).toLowerCase()) return true;
        if (u?.email && w.techEmail && String(w.techEmail).toLowerCase() === String(u.email).toLowerCase()) return true;
        return false;
      });
      const norm = (mine.length ? mine : []).map((w) => ({
        ...w,
        service: w.service || w.name || 'Service',
        customer: w.customer || w.customerName || '',
        address: w.address || [w.city, w.state, w.zip].filter(Boolean).join(', '),
        time: w.time || '',
      }));
      // upcoming/active first
      const rank = { 'In Progress': 0, 'En Route': 1, Scheduled: 2, Completed: 3, Cancelled: 4 };
      norm.sort((a, b) => (rank[a.status] ?? 5) - (rank[b.status] ?? 5));
      setJobs(norm);
      cacheJobs(norm);
      setSavedAt(null);
      setLoadError(false);
    }).catch(() => {
      // No signal: show the jobs saved on the phone at the last successful load.
      const cached = cachedJobs();
      if (cached) { setJobs(cached.jobs); setSavedAt(cached.at); setLoadError(false); }
      else setLoadError(true);
    });
  }, []);

  useEffect(() => {
    const raw = localStorage.getItem('pp_user');
    if (!raw) { navigate('/login'); return; }
    let u;
    try { u = JSON.parse(raw); } catch { localStorage.removeItem('pp_user'); navigate('/login'); return; }
    if (!u || typeof u !== 'object') { localStorage.removeItem('pp_user'); navigate('/login'); return; }
    setUser(u);
    loadJobs(u);
  }, [navigate, loadJobs]);

  // Report real GPS location so the dispatch map shows this technician live
  useEffect(() => {
    if (!navigator.geolocation) return;
    const report = () => navigator.geolocation.getCurrentPosition(
      (pos) => { team.reportLocation(pos.coords.latitude, pos.coords.longitude).catch(() => {}); },
      () => {}, { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
    report();
    const t = setInterval(report, 30000);
    return () => clearInterval(t);
  }, []);

  const logout = () => { localStorage.removeItem('pp_user'); localStorage.removeItem('xp_token'); navigate('/'); };
  if (!user) return null;

  return (
    <Routes>
      <Route index element={<TechHome user={user} onLogout={logout} jobs={jobs} loadError={loadError} savedAt={savedAt} onRetry={() => loadJobs(user)}/>}/>
      <Route path="job/:id" element={<TechJob user={user} onLogout={logout} jobs={jobs} reload={() => loadJobs(user)} updateLocalJob={updateLocalJob}/>}/>
      <Route path="messages" element={
        <div className="max-w-md mx-auto bg-white min-h-screen">
          <TechHeader user={user} onLogout={logout} title="Messages" back/>
          <MessagingView heightClass="h-[calc(100vh-72px)]" singlePane/>
        </div>
      }/>
      <Route path="ce" element={
        <div className="max-w-md mx-auto bg-slate-50 min-h-screen">
          <TechHeader user={user} onLogout={logout} title="Education" back/>
          <CEStudentView compact/>
        </div>
      }/>
    </Routes>
  );
}
